import React from "react";
import { Paper, Stack, Typography, Box, Skeleton, LinearProgress } from "@mui/material";
import BugReportIcon from "@mui/icons-material/BugReport";
import CodeIcon from "@mui/icons-material/Code";

/**
 * Card compuesta de KPI para Care Gaps y Coding Opportunities.
 * Muestra números principales + progress bar (open vs closed).
 */
export default function KpiCompositeCard({
  type,
  data,
  loading,
  onClick,
}) {
  if (loading) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          flex: 1,
        }}
      >
        <Stack spacing={1.5}>
          <Skeleton variant="text" width="40%" />
          <Skeleton variant="text" width="30%" height={36} />
          <Skeleton variant="rounded" width="100%" height={8} />
          <Stack direction="row" spacing={2}>
            <Skeleton variant="text" width="40%" />
            <Skeleton variant="text" width="40%" />
          </Stack>
        </Stack>
      </Paper>
    );
  }

  const isCareGaps = type === "careGaps";
  const config = isCareGaps
    ? {
        title: "Care Gaps",
        icon: <BugReportIcon sx={{ fontSize: 20 }} />,
        mainValue: data?.careGapsOpen || 0,
        mainLabel: "open",
        color: "#EA580C",
        bgColor: "#FFF7ED",
        progressValue:
          data?.careGapsOpen && data?.careGapsClosed
            ? (data.careGapsOpen / (data.careGapsOpen + data.careGapsClosed)) * 100
            : 0,
        stats: [
          { label: "Non-Compliant", value: data?.careGapsNonCompliant || 0, color: "#DC2626" },
          { label: "Closed", value: data?.careGapsClosed || 0, color: "#16A34A" },
        ],
      }
    : {
        title: "Coding Opportunities",
        icon: <CodeIcon sx={{ fontSize: 20 }} />,
        mainValue: data?.codingOppsNotAddressed || 0,
        mainLabel: "not addressed",
        color: "#7C3AED",
        bgColor: "#F5F3FF",
        progressValue:
          data?.codingOppsNotAddressed && data?.codingOppsTotal
            ? (data.codingOppsNotAddressed / data.codingOppsTotal) * 100
            : 0,
        stats: [
          { label: "Total", value: data?.codingOppsTotal || 0, color: "#475569" },
          {
            label: "Addressed",
            value: (data?.codingOppsTotal || 0) - (data?.codingOppsNotAddressed || 0),
            color: "#16A34A",
          },
        ],
      };

  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        p: 2.5,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        flex: 1,
        cursor: onClick ? "pointer" : "default",
        transition: "all 200ms ease",
        "&:hover": onClick
          ? {
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              borderColor: config.color,
              transform: "translateY(-2px)",
            }
          : {},
      }}
    >
      <Stack spacing={1.5}>
        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 32,
              height: 32,
              borderRadius: 1.5,
              bgcolor: config.bgColor,
              color: config.color,
            }}
          >
            {config.icon}
          </Box>
          <Typography
            variant="body2"
            fontWeight={600}
            color="text.secondary"
            sx={{ textTransform: "uppercase", letterSpacing: 0.5, fontSize: "0.7rem" }}
          >
            {config.title}
          </Typography>
        </Stack>

        {/* Main number */}
        <Stack direction="row" alignItems="baseline" spacing={0.5}>
          <Typography variant="h4" fontWeight={800} sx={{ color: config.color, lineHeight: 1.1 }}>
            {config.mainValue.toLocaleString()}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {config.mainLabel}
          </Typography>
        </Stack>

        {/* Progress bar */}
        <Box>
          <LinearProgress
            variant="determinate"
            value={Math.min(config.progressValue, 100)}
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: "#F1F5F9",
              "& .MuiLinearProgress-bar": {
                borderRadius: 3,
                bgcolor: config.color,
              },
            }}
          />
        </Box>

        {/* Sub-stats */}
        <Stack direction="row" spacing={2} divider={<Box sx={{ borderLeft: "1px solid", borderColor: "divider" }} />}>
          {config.stats.map((stat) => (
            <Stack key={stat.label} direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                {stat.label}:
              </Typography>
              <Typography variant="caption" fontWeight={700} sx={{ color: stat.color, fontSize: "0.7rem" }}>
                {stat.value.toLocaleString()}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Paper>
  );
}
